import { CSSProperties, useCallback, useEffect, useState } from "react";
import { Button, Spinner } from "react-bootstrap";
import Moment from "react-moment";
import { Tooltip } from "react-tooltip";
import { getPhColour } from "./color-utils";
import { DAFSensorPanel } from "./DAFSensorPanel";
import "./DAFVisualisation.css";
import { MOMENT_DATE_FORMAT } from "./date-utils";
import Diagram from "./Diagram";
import TimeRangeSelector from "./TimeRangeSelector";
import { formatDecimalNumber } from "./format-utils";
import { DAFState, Mode, SensorType, UnitLabels } from "./types";
import TssDensityScale from './TssDensityScale';
import React from "react";

interface DAFVisualiserCSSProps extends CSSProperties {
  "--daf-inlet": string;
  "--daf-outlet": string;
}

const getCssProps = (daf?: DAFState, selectedMode?: Mode): DAFVisualiserCSSProps => {
  if (!daf) {
    return {
      "--daf-inlet": "#cccccc",
      "--daf-outlet": "#cccccc",
    };
    }
    if (selectedMode === Mode.TSS) {
        return {
            "--daf-inlet": "#7FDCFF",//getPhColour(daf.inletPH.value),
            "--daf-outlet": "#7FDCFF",//getPhColour(daf.outletPH.value),
        };
    }
    return {
        "--daf-inlet": getPhColour(daf.inletPH.value),
        "--daf-outlet": getPhColour(daf.outletPH.value),
    };
};

export type DAFVisualisationProps = {
    dafData: DAFState[];
    selectedLiveOption: string;
    liveOptions: string[];
    changeLiveOption: (value: string) => void;
    changeCustomTimeRange: (starttime: string, endtime: string) => void;
    loadingData: boolean;
    selectedMode?: Mode;
};



const ONE_DAY_IN_MS = 1000 * 60 * 60 * 24;

type ExtendedDAFState = DAFState & {
  isOff: boolean;
  hasAlarm: boolean;
  parsedTimestamp: Date;
  PHPercent: number;
  hasPreviousState: boolean;
  halfwayToPreviousPH: number;
  hasNextState: boolean;
  halfwayToNextPH: number;
};

const processDafData = (dafData: DAFState[], pHSelection: SensorType) => {
  const data: ExtendedDAFState[] = dafData.map((x) => ({
    ...x,
    isOff: !x.flowrate.value,
    hasAlarm:
      x.outletTSS?.alarm ||
      x.outletPH?.alarm ||
      x.flowrate?.alarm ||
      x.inletTSS?.alarm ||
      x.inletPH?.alarm,
    parsedTimestamp: new Date(x.timestamp),
    PHPercent: 0,
    hasPreviousState: false,
    halfwayToPreviousPH: 0,
    hasNextState: false,
    halfwayToNextPH: 0,
  }));

  const aggregates = getAggregates(data, pHSelection);

  // do a run through, setting the properties on each item that relate to the next and previous items
  for (let i = 0; i < data.length; i++) {
    const thisData = data[i];
    const lastData = data[i - 1];

    if (pHSelection === SensorType.InletPH) {
        thisData.PHPercent =
            (thisData.inletPH?.value - aggregates.minPHNormalised) /
            aggregates.PHNormalisedRange;
    }
    else {
        thisData.PHPercent =
            (thisData.outletPH?.value - aggregates.minPHNormalised) /
            aggregates.PHNormalisedRange;
    }
    

    if (!lastData) {
      // if no previous, can't compare current and previous, move onto the next one
      continue;
    }

    /*
      We have a previous. That means we can do two different sets of comparisons
      set current = thisData and previous = lastData, then set toPrevious on current (which is index)
      set current = lastData and next = thisData, then set the toNext on "current" (which is index - 1)

      A diagram for what we will achieve using these - same diagram as in the CSS
      +------+-----+
      |      |     |
      |   •--+     | reading 1
      |   |\ |     |
      |   | \|     |
      |   +--•--+  | half way point
      |      |\ |  | 
      |      | \|  | 
      |      +--•  | reading 2
      |      |     |
      +------+-----+

      Except, this will all fall apart if the current reading is the same as the previous or next reading, because the height of the element will be 0, so there's nowhere for the gradient background.
      (Actually, Dan found a solution for this, skip to the ALL CAPS SECTION below and stop reading, the rest of below is Dan spitballing alternative solutions)

      So, we could have an "is same" state, and just draw a straight line using background color or border.
      But, it'll also fall apart if the current reading is sort of the same as the previous reading, but not exactly the same.
      So, we could have some sort of threshold.
      The threshold is, practically, 1px. But we're not dealing with pixels, we're dealing with percentages.
      We don't know what percent of the div is 1px. Well... we could, but that involves shared knowledge that the tss sparkline square is 32px high
      I'd rather not have that shared knowledge.
      I'd also rather not set it here using a variable, feels like the sort of thing the css should be in charge of.

      So... it's time for trigonometry to work out the angle to rotate the element with.
      Using SOH CAH TOA, we can get the angle.
      We know Opposite and Adjacent, so we can use Tan. Which involves ArcTan. MDN suggests atan2, there's differences, something about theta
      https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Math/atan2

                      y
                      |   /|
                      |  / |
                      | /  | Opposite
                      |/θ  |
        -x -----------•----------- x
                      |  ^
                      | Adjacent  
                      |
                    -y
        
        This is for a line that starts from the current reading, so the current reading is 0,0
        For current to next, 
          opposite = half way point to next minus current.
          Adjacent = 50
        For current to previous, opposite = current minus half way point to previous. Adjacent is -50.
        atan2 can handle negatives just fine, it works for any quadrant.
        atan2 takes in the points on the quadrant, where Adjacent is x and Opposite is y

        EXCEPT... This only works if we are dealing with a square. Which we will not be. Good idea for squares, but no.
        +------------------------+    +---+
        |                        | vs |   |
        +------------------------+    +---+

        So, back to the boxes with the gradient lines. And set the 32px height here so we can work out if we are straight line or gradient.

        Or declare ENOUGH OF THIS NONSENSE already and just use a canvas? Means the tooltip has to respond to a canvas.
        Maybe we can put the canvas position absolute over the top of the sparklines, z-index it back a bit, tooltip will still work.
        Nope. Canvas sizing is a pain. Really need to know the width and height of it in pixels before we can do anything with it.
        It can, in theory, do scaling, but that can also render messy. And there's high dpi to take into account. Ugh.

        Or... make an SVG for the whole lot?

        The svg needs
        - One rect per sparkline, so we can mouseover and highlight the "you are here"
        - One rect per sparkline ph, so we can put the colour on it.
          I guess it could be a background gradient on the main one using some "from this ph colour to this ph colour", but math
          That'd involve the same sort of "half way point" so that we could gradate from the previous to the next, with the center point being the middle of the rect
          ie: the gradient would be half way to previous value at 0%, current value at 50%, half way to next value at 100%
          and some messing around with white/transparent for "there is no previous" "there is no next"

        - One path for the graph lines. Paths can move and line, so we can skip the ones that are empty
        - One circle per sparkline so we can put the tss value on it
           -OR- One path with lots of arcs on it for the tss values - https://www.smashingmagazine.com/2019/03/svg-circle-decomposition-paths/
        - A reused "icon" for the alarm and off states. the <use> element for this.

        Positioning/coordinates are a bit easier with svg because of the view box.
        Set the view box to, say, 0 0 100 1000 and we base everything off that. It'll get weird if the svg gets too resized.

        AT THIS POINT DANIEL SLAPPED HIMSELF SO HARD UPSIDE THE BACK OF THE HEAD, HIS HEAD LANDED IN CAMBODIA
        For the diagonal lines solution, set a min height on the element of 1px. That way, even if the height works out to be less than 1 pixel, the min-height kicks in.
        Nothing breaks. Can I have the half a day back I've spent overthinking this?
    */

    {
      // new scope block because we want current and next variable names, but they mean different things in the two blocks

      /* set current = thisData and previous = lastData, then set toPrevious on current (which is index) */
      /* for current to next
          C     N
      +------+-----+
      |      |     |
      |   •--+     | C   = 75%
      |   |\ |     |
      |   | \|     |
      |   +--•--+  | half way point = C - ((C-N)/2) = 75 - ((75-15)/2) = 75 - 30 = 45%
      |      |\ |  | 
      |      | \|  | 
      |      +--•  | N   =15%
      |      |     |
      +------+-----+    

      C   N  Delta/2 Halfway 
      75  15   30      45      slope down - 75 -> 45 -> 15
      15  75  -30      45      slope up - 15 -> 45 -> 75
      75  75    0      75      flat - 75 -> 75 -> 75
    */

      const current = lastData;
      const next = thisData;

      // we might have a next state, but if it's off then it basically doesn't exist. Can't compare to it.
      current.hasNextState = true;

      const halfwayPoint =
        current.PHPercent -
          (current.PHPercent - next.PHPercent) / 2;
          current.halfwayToNextPH = halfwayPoint;
    }

    {
      // new scope block because we want current and next variable names, but they mean different things in the two blocks

      /* for current to prev
          P     C
      +------+-----+
      |      |     |
      |   •--+     | P   = 75%
      |   |\ |     |
      |   | \|     |
      |   +--•--+  | half way point = C - ((C-P)/2) = 15 - (-60/2) = 15 - -30 = 45%
      |      |\ |  | 
      |      | \|  | 
      |      +--•  | C   =15%
      |      |     |
      +------+-----+    

      C   P  Delta/2 Halfway 
      15  75  -30      45      slope up
      75  15   30      45      slope down
      75  75    0      75      flat

    */

      /* set current = thisData and previous = lastData, then set toPrevious on current (which is index) */
      const current = thisData;
      const previous = lastData;

      // we might have a previous state, but if it's off then it basically doesn't exist. Can't compare to it.
      current.hasPreviousState = true;

      const halfwayPoint =
          current.PHPercent -
          (current.PHPercent - previous.PHPercent) / 2;
          current.halfwayToPreviousPH = halfwayPoint;
    }
  }

  return data;
};

const getAggregates = (data: DAFState[], pHSelection: SensorType) => {
  let minInputTSS = Number.MAX_VALUE;
  let maxInputTSS = Number.MIN_VALUE;
  let minInputPH = Number.MAX_VALUE;
  let maxInputPH = Number.MIN_VALUE;
  let minOutputTSS = Number.MAX_VALUE;
  let maxOutputTSS = Number.MIN_VALUE;
  let minOutputPH = Number.MAX_VALUE;
  let maxOutputPH = Number.MIN_VALUE;
  let minFlowrate = Number.MAX_VALUE;
  let maxFlowrate = Number.MIN_VALUE;
  for (const daf of data) {
    // ignore the times where it is switched off
    if (daf.flowrate.value) {
      minInputTSS = Math.min(minInputTSS, daf.inletTSS?.value);
      maxInputTSS = Math.max(maxInputTSS, daf.inletTSS?.value);
      minInputPH = Math.min(minInputPH, daf.inletPH?.value);
      maxInputPH = Math.max(maxInputPH, daf.inletPH?.value);
      minOutputTSS = Math.min(minOutputTSS, daf.outletTSS?.value);
      maxOutputTSS = Math.max(maxOutputTSS, daf.outletTSS?.value);
      minOutputPH = Math.min(minOutputPH, daf.outletPH?.value);
      maxOutputPH = Math.max(maxOutputPH, daf.outletPH?.value);
      minFlowrate = Math.min(minFlowrate, daf.flowrate?.value);
      maxFlowrate = Math.max(maxFlowrate, daf.flowrate?.value);
    }
  }
  // for the purposes of max and min, we don't want the minimum value to be exactly 0%
  // or the max 100%
  // we want some breathing room around the edge, so that if we were to draw a legend, it'd start on a nice neat number
    let minPHNormalised;
    let maxPHNormalised;
    if (pHSelection === SensorType.InletPH) {
        minPHNormalised = Math.floor(minInputPH) - 0.5;
        maxPHNormalised = Math.ceil(maxInputPH) + 0.5;
    }
    else {
        minPHNormalised = Math.floor(minOutputPH) - 0.5;
        maxPHNormalised = Math.ceil(maxOutputPH) + 0.5;
    }
    const PHNormalisedRange =
        maxPHNormalised - minPHNormalised;
  return {
    minInputTSS,
    maxInputTSS,
    minInputPH,
    maxInputPH,
    minOutputTSS,
    maxOutputTSS,
    minPHNormalised,
    maxPHNormalised,
    PHNormalisedRange,
    minOutputPH,
    maxOutputPH,
    minFlowrate,
    maxFlowrate,
  };
};

export const DAFVisualisation = ({
    dafData: rawDafData,
    selectedLiveOption,
    liveOptions,
    changeLiveOption,
    changeCustomTimeRange,
    loadingData,
}: DAFVisualisationProps) => {
    const [selectedParam, setSelectedParam] = useState<SensorType>(2);
    const [isPaused, setIsPaused] = useState(false);
    const [processedData, setProcessedData] = useState<ExtendedDAFState[]>(
        processDafData(rawDafData, selectedParam)
    );
    const [expanded, setExpanded] = useState(true);
    const [selectedMode, setSelectedMode] = useState<Mode>(Mode.Combination);

  const [cssProps, setCssProps] = useState<DAFVisualiserCSSProps>(
    getCssProps()
  );

  const [selectedDAF, setSelectedDAF] = useState<DAFState | null>(null);

  const [selectedIndex, setSelectedIndex] = useState(0);

  const handleIndexChange = useCallback(
    (value: number) => {
      // make sure the controlled value is correct
      setSelectedIndex(value);

      // update the selected DAF frame we're displaying
      setSelectedDAF(processedData[value]);

      // update the css props
          // TODO cssProps could be useMemo'd around selectedDAF
          setCssProps(getCssProps(processedData[value], selectedMode));

      // if the index is not the last one, auto pause
      setIsPaused(value !== processedData.length - 1);

      // This whole thing is bugging me already
      // Might be better if we assume we area always showing the last item
      // and if they set the slider to something else, set that ITEM as an overrideItem
      // having to set the index AND set the item feels a bit redundant
      // having to check disableAutoScroll feels dicey as heck
    },
    [processedData, selectedMode]
  );

    useEffect(() => {
    if (rawDafData.length < 1) {
        // no data, reset everything
        resetVisualisation();
        return;
    };

    if (isPaused) {
      // drop on the floor. It's still here in the rawDafData prop when they hit resume
      return;
    }

    // run some calculations on the data, enhance it with all sorts of extra info for display
    const data = processDafData(rawDafData, selectedParam);
    // tuck it away in state
    setProcessedData(data);

    // NOPE, do not try and call handleIndexChange here
    // otherwise we go around and around in circles - handleIndexChange has processedData as a dependency
    // so if as soon as we call setProcessedData, it triggers this effect again.
    // Rather, we'll do exactly the same as what is inside handleIndexChange. Sad panda face.
    // handleIndexChange(data.length - 1);
    const latestData = data[data.length - 1];
    setSelectedDAF(latestData);
    setSelectedIndex(data.length - 1);
        setCssProps(getCssProps(latestData, selectedMode));
        console.log(rawDafData);
        console.log(processedData);
  }, [rawDafData, isPaused, selectedParam, selectedMode]);

  const canGoPrevious = selectedIndex > 0;
  const canGoNext = selectedIndex < processedData.length - 1;

    const resetVisualisation = () => {
        setProcessedData([]);
        setSelectedDAF(null);
        setSelectedIndex(0);
        setIsPaused(false);
        console.log("nowwww");
        console.log(rawDafData);
        console.log(processedData);
    } 

  const goPrevious = () => {
    setIsPaused(true);
    handleIndexChange(selectedIndex - 1);
  };

  const goNext = () => {
    setIsPaused(true);
    handleIndexChange(selectedIndex + 1);
  };

  const doPause = () => {
    setIsPaused(true);
  };

  const doPlay = () => {
    setIsPaused(false);
    };

    

  return (
    <div className="daf" style={{ ...cssProps }}>
      <div
        className={`daf__diagram ${
          selectedDAF?.flowrate.value ? "" : "--off"
        } ${!selectedDAF ? "--no-selected-reading" : ""}`}
          >
            <div>
                  {(selectedDAF?.outletTSS || selectedDAF?.inletTSS) && <div className="param-button-group">
                      <button
                          className={`param-button left ${selectedMode === Mode.PH ? 'selected' : ''}`}
                          onClick={() => setSelectedMode(Mode.PH)}
                      >
                          pH
                      </button>
                      <button
                          className={`param-button ${selectedMode === Mode.Combination ? 'selected' : ''}`}
                          onClick={() => setSelectedMode(Mode.Combination)}
                      >
                          +
                      </button>
                      <button
                          className={`param-button right ${selectedMode === Mode.TSS ? 'selected' : ''}`}
                          onClick={() => setSelectedMode(Mode.TSS)}
                      >
                          {selectedDAF?.outletTSS?.label || "TSS"}
                      </button>
                  </div>}
              {processedData === null || processedData.length === 0 || loadingData ? <Spinner style={{ marginTop: 10 }} animation="border" role="status" /> : ''}
              </div>
              <Diagram
                  style={{ height: "100%", maxWidth: "97%" }}
                  animate={Boolean(selectedDAF?.flowrate?.value)}
                  includeTSS={selectedMode !== Mode.PH}
                  inletTSSPercent={Math.min(100, (selectedDAF && selectedDAF.inletTSS) ? (selectedDAF.inletTSS?.value * 100 / (selectedDAF.inletTSS?.max ? selectedDAF.inletTSS?.max : 10000) ): 0)}
                  outletTSSPercent={Math.min(100, (selectedDAF && selectedDAF.outletTSS) ? (selectedDAF.outletTSS?.value * 100 / (selectedDAF.outletTSS?.max ? selectedDAF.outletTSS?.max : 10000 )): 0)}
              />
              {(selectedDAF?.outletPH || selectedDAF?.inletPH) ? <div style={{
            display: 'flex', flexDirection: 'column', width: '40px', marginLeft: '100px', alignItems: 'center', justifyContent: 'center', }}>
            <div
                key={'title'}
                onClick={() => setExpanded(true)}
                style={{
                    position: 'relative',
                    height: '30px',
                    width: '65px',
                    marginBottom: '0px',
                    cursor: 'pointer',
                }}
            >
                {/* Pill background */}
                <div
                    style={{
                        height: '100%',
                        width: '100%',
                        backgroundColor: expanded ? 'white' : 'black',
                        borderRadius: '20px',
                        border: `2px solid black`,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: '12px',
                        fontWeight: 'bold',
                        color: expanded ? 'black' : 'white',
                        boxShadow: '0 1px 2px rgba(0,0,0,0.15)',
                        userSelect: 'none',    // ← prevents highlight
                        cursor: 'pointer',     // ← makes it feel like a button
                        outline: 'none',
                        transition: 'all 0.2s ease-in-out',
                    }}
                >{'pH Scale'}</div> </div>
            {expanded && Array.from({ length: 15 }, (_, i) => (
        <div
            key={i}
            style={{
                position: 'relative',
                height: '25px',
                width: '60px',
                marginBottom: '0px',
                transition: 'all 0.2s ease-in-out',
            }}
        >
            {/* Pill background */}
            <div
                style={{
                    height: '100%',
                    width: '100%',
                    backgroundColor: getPhColour(i),
                    borderRadius: '12.5px',
                }}
            />

            {/* White circle inside pill */}
            <div
                style={{
                    position: 'absolute',
                    right: '0',
                    top: '50%',
                    transform: 'translate(0%, -50%)',
                    backgroundColor: 'white',
                    height: '100%',
                    width: '25px',
                    borderRadius: '12.5px',
                    border: `2px solid ${getPhColour(i)}`,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '12px',
                    fontWeight: 'bold',
                    boxShadow: '0 1px 2px rgba(0,0,0,0.15)',
                }}
            >
                {i}
            </div>
        </div>
            ))}
                  
              </div> : <></>}
              {(selectedDAF?.outletTSS || selectedDAF?.inletTSS) ? <TssDensityScale label={selectedDAF?.outletTSS?.label || "TSS"} /> : <></>}
      </div>
      <div className="daf__info">
        <DAFSensorPanel
          sensorType="inlet"
            dafData={selectedDAF}
            className="daf__info__panel --inlet"
            selected={selectedParam === 1}
            changeSelection={() => setSelectedParam(1)}
        />
        <DAFSensorPanel
          sensorType="outlet"
            dafData={selectedDAF}
            className="daf__info__panel --outlet"
            selected={selectedParam === 2}
            changeSelection={() => setSelectedParam(2)}
        />
        <div className="daf__info__slider">
          {processedData.length < 1 && (
            <div className="daf-slider --no-readings">No data</div>
          )}
          {processedData.length === 1 && (
            <div className="daf-slider --one-reading">Only one data point</div>
          )}
          {processedData.length > 1 && (
            <div className="daf-slider">
              <div className="daf-slider__sparklines">
                {processedData.map((daf, index) => {
                  const isSelected =
                    (daf.key ?? daf.timestamp) ===
                    (selectedDAF?.key ?? selectedDAF?.timestamp);

                  const classes = [];
                  const phClasses = [];
                  if (isSelected) {
                    classes.push("--selected");
                  }
                  if (daf.hasAlarm) {
                    classes.push("--alarm");
                  }

                  if (daf.isOff) {
                    classes.push("--off");
                  } else {
                    classes.push("--on");
                  }

                  if (daf.hasPreviousState) {
                      phClasses.push("--has-previous");
                    if (daf.halfwayToPreviousPH < daf.PHPercent) {
                        phClasses.push("--previous-less-than-current");
                    } else {
                        phClasses.push("--previous-greater-than-current");
                    }
                  }

                  if (daf.hasNextState) {
                      phClasses.push("--has-next");
                      if (daf.halfwayToNextPH < daf.PHPercent) {
                          phClasses.push("--next-less-than-current");
                    } else {
                          phClasses.push("--next-greater-than-current");
                    }
                  }

                  return (
                    <div
                      key={daf.key ?? daf.timestamp}
                      className={`daf-slider__sparklines__item ${classes.join(
                        " "
                      )}`}
                      data-tooltip-content={index.toString()}
                      data-tooltip-id="sparkline-tooltip"
                      onClick={() => handleIndexChange(index)}
                    >
                      <div
                              className={`daf-slider__sparklines__item__tss ${phClasses.join(
                          " "
                        )}`}
                        style={
                          {
                            "--outlet-ph-percent": daf.PHPercent,
                            "--halfway-to-previous-outlet-ph-percent":
                              daf.halfwayToPreviousPH,
                            "--halfway-to-next-outlet-ph-percent":
                              daf.halfwayToNextPH,
                          } as CSSProperties
                        }
                      ></div>
                      <div
                        className="daf-slider__sparklines__item__ph"
                              style={{
                                  backgroundColor: getPhColour(selectedParam === SensorType.InletPH ? daf.inletPH.value : daf.outletPH.value),
                        }}
                      ></div>
                      <div className="daf-slider__sparklines__item__alert">
                        {!daf.isOff && daf.hasAlarm ? (
                          <span className="pi pi-exclamation-triangle blinkWarning"></span>
                        ) : null}
                        {daf.isOff && daf.hasAlarm ? (
                          <span className="pi pi-power-off blinkWarning"></span>
                        ) : null}
                        {daf.isOff && !daf.hasAlarm ? (
                          <span className="pi pi-power-off"></span>
                        ) : null}
                      </div>
                    </div>
                  );
                })}
                {/* see the render doco
              https://react-tooltip.com/docs/examples/render
              We've put the index in the data-tooltip-content property
              and use that to get the actual daf data. Much better than trying to string concat 
              into the data-tooltip-content property, or having one <Tooltip> for each item
            */}
                <Tooltip
                  id="sparkline-tooltip"
                  render={({ content }) => {
                    if (content === null || content === "") {
                      return undefined;
                    }
                    const index = parseInt(content, 10);
                    if (isNaN(index)) {
                      return undefined;
                    }
                    const daf = processedData[index];
                    return (
                      <span>
                        <Moment fromNow>{daf?.timestamp}</Moment> -{" "}
                        <Moment interval={0} format={MOMENT_DATE_FORMAT}>
                          {daf?.timestamp}
                        </Moment>
                        <br />
                            Inlet {daf.inletTSS?.label || "TSS"}: {formatDecimalNumber(
                          daf.inletTSS?.value
                        )}{" "}
                            {UnitLabels[daf.inletTSS?.unit ?? -1] ?? "mg/L"}{" "}
                        {daf.inletTSS?.alarm ? (
                          <>
                            <span className="pi pi-exclamation-triangle blinkWarning"></span>
                            <br />
                            {daf.inletTSS?.alarmMessage}
                          </>
                        ) : null}
                        <br />
                            Outlet {daf.outletTSS?.label || "TSS"}: {formatDecimalNumber(
                          daf.outletTSS?.value
                        )}{" "}
                            {UnitLabels[daf.outletTSS?.unit ?? -1] ?? "mg/L"}{" "}
                        {daf.outletTSS?.alarm ? (
                          <>
                            <span className="pi pi-exclamation-triangle blinkWarning"></span>
                            <br />
                            {daf.outletTSS?.alarmMessage}
                          </>
                        ) : null}
                        <br />
                        Inlet pH: {formatDecimalNumber(daf.inletPH?.value)}
                        {daf.inletPH?.alarm ? (
                          <>
                            <span className="pi pi-exclamation-triangle blinkWarning"></span>
                            <br />
                            {daf.inletPH?.alarmMessage}
                          </>
                        ) : null}
                        <br />
                        Outlet pH: {formatDecimalNumber(daf.outletPH?.value)}
                        {daf.outletPH?.alarm ? (
                          <>
                            <span className="pi pi-exclamation-triangle blinkWarning"></span>
                            <br />
                            {daf.outletPH?.alarmMessage}
                          </>
                        ) : null}
                        <br />
                        Flowrate:{" "}
                        {daf.flowrate?.value ? (
                          <>{formatDecimalNumber(daf.flowrate?.value)} kL/hr </>
                        ) : (
                          "No flow "
                        )}{" "}
                        {daf.flowrate?.alarm ? (
                          <>
                            <span className="pi pi-exclamation-triangle blinkWarning"></span>
                            <br />
                            {daf.flowrate?.alarmMessage}
                          </>
                        ) : null}
                      </span>
                    );
                  }}
                />
              </div>

              <div className="daf-slider__controls">
                <Button
                  variant="light"
                  onClick={() => goPrevious()}
                  disabled={!canGoPrevious}
                >
                  <span className="pi pi-step-backward"></span>
                </Button>
                {!isPaused && (
                  <Button variant="light" onClick={() => doPause()}>
                    <span className="pi pi-pause"></span>
                  </Button>
                )}
                {isPaused && (
                  <Button variant="light" onClick={() => doPlay()}>
                    <span className="pi pi-play"></span>
                  </Button>
                )}
                <Button
                  variant="light"
                  onClick={() => goNext()}
                  disabled={!canGoNext}
                >
                  <span className="pi pi-step-forward"></span>
                </Button>
              </div>
              <div className="daf-slider__info">
                {selectedDAF && (
                  <span
                    className={`daf-slider__info__time ${isPaused || !liveOptions.includes(selectedLiveOption) ? "--paused" : ""
                    }`}
                  >
                    <Moment interval={0} format={MOMENT_DATE_FORMAT}>
                      {selectedDAF.timestamp}
                    </Moment>
                  </span>
                )}
                </div>
                <div className="daf-slider__timerange">
                              <TimeRangeSelector
                                  selectedLiveOption={selectedLiveOption}
                                  liveOptions={liveOptions}
                                  changeLiveOption={changeLiveOption}
                                  changeCustomTimeRange={changeCustomTimeRange}
                                  resetVisualisation={resetVisualisation}
                                />
                </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
